import React, { useState, useEffect } from 'react';
import '../styles/Popup.css';
import { MdArrowForwardIos } from "react-icons/md";
import { VscDiscard } from "react-icons/vsc";
import { MdArrowBackIos } from "react-icons/md";

const Popup = ({ onClose, onSubmit }) => {
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        question1: '',
        question2: '',
        question3: '',
    });
    const [placeholderSuggestions, setPlaceholderSuggestions] = useState([]);
    const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0);
    const staticStep1Placeholders = [
        "I need some advice on budgeting my monthly expenses",
        "Can you suggest healthy meal ideas for a busy week?",
        "What are some effective ways to reduce stress?",
        "I'm looking for tips on improving my sleep quality",
        "How can I organize my home more efficiently?"
    ];
    useEffect(() => {
        const suggestions = step === 1 ? staticStep1Placeholders : placeholderSuggestions;
        if (suggestions.length === 0) return;

        const interval = setInterval(() => {
            setCurrentPlaceholderIndex(prev => 
                (prev + 1) % suggestions.length
            );
        }, 1800); // Rotate every 3 seconds

        return () => clearInterval(interval);
    }, [step, placeholderSuggestions]);
    
    const SOCKET_SERVER_URL = process.env.REACT_APP_BACKEND_URL;
    const API_ENDPOINT = `${SOCKET_SERVER_URL}/api/chats/generatePlaceholders`;

    const fetchPlaceholders = async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000); // 15s timeout

        try {
            const payload = {
                step,
                answers: {
                    what: formData.question1,
                    ...(step === 3 && { why: formData.question2 })
                }
            };

            const response = await fetch(API_ENDPOINT, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify(payload),
                signal: controller.signal,
            });

            clearTimeout(timeoutId); // Clear timeout if request succeeds

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            
            const contentType = response.headers.get('content-type');
            if (!contentType?.includes('application/json')) {
                throw new Error('Invalid response format');
            }

            const data = await response.json();
            const cleanedSuggestions = (data.suggestions || []).map(suggestion =>
                suggestion.replace(/"/g, '')
              );
            setPlaceholderSuggestions(cleanedSuggestions || []);
            setCurrentPlaceholderIndex(0);

        } catch (error) {
            console.error('Placeholder fetch failed:', error);
            setPlaceholderSuggestions([
                "How detailed would you like the response to be?",
                "Should I include code examples?",
                "Would you prefer bullet points or paragraph format?",
                "Any specific technologies to focus on?",
                "Need visual diagrams or just text?"
            ]);
        }
    };

    useEffect(() => {
        if (step > 1) fetchPlaceholders();
    }, [step]); // Only run when `step` changes

    useEffect(() => {
        if (placeholderSuggestions.length === 0) return;
        
        const interval = setInterval(() => {
            setCurrentPlaceholderIndex(prev => 
                (prev + 1) % placeholderSuggestions.length
            );
        }, calculateIntervalDuration());

        return () => clearInterval(interval);
    }, [placeholderSuggestions]); // Removed `currentPlaceholderIndex` to prevent re-renders

    const calculateIntervalDuration = () => {
        const text = placeholderSuggestions[currentPlaceholderIndex] || '';
        return Math.max(3000, Math.min(8000, text.length * 100));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleNext = () => setStep((prev) => prev + 1);
    const handlePrev = () => setStep((prev) => prev - 1);
    const handleSubmit = () => {
        console.log('Form Data:', formData);
        onSubmit(formData);
        onClose();
    };

    const handleSkipSubmit = () => {
        setFormData((prev) => ({
            ...prev,
            question3: '' // Clear the last message
        }));
        onSubmit({ ...formData, question3: '' }); // Ensure last message is empty
        onClose();
    };

    return (
        <div className="popup-overlay">
            <div className="popup-content">
                {step === 1 && (
                    <>
                        <h2>Hey Love, what would you like to do today?</h2>
                        <div className="input-wrapper">
                            <textarea
                                name="question1"
                                value={formData.question1}
                                onChange={handleInputChange}
                                placeholder={`${staticStep1Placeholders[currentPlaceholderIndex]}`}
                                className="popup-textarea"
                                rows={2} 
                            />
                        </div>
                        <div className="popup-buttons">
                            {/* <button onClick={onClose} className="cancel"><VscDiscard/></button> */}
                            <button onClick={handleNext} disabled={!formData.question1.trim()}>Next <MdArrowForwardIos/></button>
                        </div>
                    </>
                )}

                {step === 2 && (
                    <>
                        <h2>Okay Love, please give me some more details</h2>
                        <div className="input-wrapper">
                            <textarea
                                name="question2"
                                value={formData.question2}
                                onChange={handleInputChange}
                                placeholder={placeholderSuggestions[currentPlaceholderIndex] || ''}
                                className="popup-textarea"
                                rows={3} 
                            />
                        </div>
                        <div className="popup-buttons">
                            <button onClick={handlePrev}><MdArrowBackIos/> Prev</button>
                            <button onClick={handleNext} disabled={!formData.question2.trim()}>Next <MdArrowForwardIos/></button>
                        </div>
                    </>
                )}

                {step === 3 && (
                    <>
                        <h2>Finally, in what Loving form do you need the answer?</h2>
                        <div className="input-wrapper">
                            <textarea
                                name="question3"
                                value={formData.question3}
                                onChange={handleInputChange}
                                placeholder={placeholderSuggestions[currentPlaceholderIndex] || ''}
                                className="popup-textarea"
                                rows={3} 
                            />
                        </div>
                        <div className="popup-buttons">
                            <button onClick={handlePrev}><MdArrowBackIos/> Prev</button>
                            {/* Enable Skip & Submit when text is EMPTY */}
                            <button onClick={handleSkipSubmit} disabled={formData.question3.trim().length > 0}>
                                Skip & Submit
                            </button>
                            {/* Enable Submit when text is NOT empty */}
                            <button onClick={handleSubmit} disabled={!formData.question3.trim()}>
                                Submit
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Popup;
